'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

// Components
import BlurImage from '@/components/shared/blur-image'
import LocaleSwitcher from '@/components/layout/header/locale-switcher'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'

// Config
import { siteConfig } from '@/config/site'
import { navConfig } from '@/config/nav'

// Utils
import { cn, placeholderBlurhash } from '@/lib/utils'

// Types
import type { Locale } from '@/i18n-config'

export function MainNav({ lang }: { lang: Locale }) {
  const pathname = usePathname()

  return (
    <div className="hidden w-full items-center justify-between py-2 md:flex">
      <Link href={`/${lang}`} className="mr-4 flex items-center gap-2 lg:mr-0">
        <BlurImage
          alt={siteConfig.title}
          blurDataURL={placeholderBlurhash}
          width={250}
          height={42}
          priority={true}
          placeholder="blur"
          className="invert"
          src="/assets/images/logo.png"
        />
        {/* <Icons.logo className="size-24" /> */}
        <span className="sr-only">{siteConfig.name} - Home page</span>
      </Link>
      <NavigationMenu className="flex items-center justify-end gap-4 md:flex-1 xl:gap-6 ">
        <NavigationMenuList className="flex items-center justify-end gap-2 md:flex-1  ">
          {Object.entries(navConfig[lang] || {}).map(([key, item]) => {
            if ('subPaths' in item) {
              return (
                <NavigationMenuItem key={key}>
                  <NavigationMenuTrigger
                    className={cn(
                      'text-primary/70 data-[state=open]:text-primary',
                      item.subPaths.some((sub) =>
                        pathname.startsWith(sub.href)
                      ) && 'text-primary'
                    )}
                  >
                    {item.title}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="">
                    <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                      <li className="row-span-3">
                        <NavigationMenuLink
                          asChild
                          active={pathname === `/${lang}`}
                        >
                          <Link
                            href={`/${lang}`}
                            className="flex size-full select-none flex-col justify-center rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
                          >
                            <div className="mb-2">
                              <BlurImage
                                alt={siteConfig.title}
                                blurDataURL={placeholderBlurhash}
                                width={250}
                                height={42}
                                priority={true}
                                placeholder="blur"
                                className="invert"
                                src="/assets/images/logo.png"
                              />
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      {item.subPaths.map(
                        (
                          sub: {
                            title: string
                            href: string
                            description: string
                          },
                          subIndex: number
                        ) => (
                          <ListItem
                            key={subIndex}
                            href={sub.href}
                            title={sub.title}
                            className={cn(pathname === sub.href && 'bg-accent')}
                          >
                            {sub.description}
                          </ListItem>
                        )
                      )}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            } else {
              return (
                <NavigationMenuItem key={key}>
                  <NavigationMenuLink asChild>
                    <Link
                      href={item.href}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        pathname === item.href
                          ? 'text-primary'
                          : 'text-primary/70'
                      )}
                    >
                      {item.title}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )
            }
          })}
        </NavigationMenuList>
        <LocaleSwitcher />
      </NavigationMenu>
    </div>
  )
}

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, title, children, ...props }, ref) => {
  // Extract href from props
  const { href, ...rest } = props
  return (
    <li>
      <Link
        ref={ref}
        href={href || '/'}
        className={cn(
          'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
          className
        )}
        {...rest}
      >
        <div className="text-sm font-medium leading-none">{title}</div>
        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
          {children}
        </p>
      </Link>
    </li>
  )
})
ListItem.displayName = 'ListItem'
